import React, { useEffect, useLayoutEffect, useState } from "react";

import "./App.css";

import ProcessBarSection from "./components/ProcessBarSection/PopupBoxSection";
import axios from "axios";
import { getStorageItem, setStorageItem } from "./utils/storageFunction";
import { detectDeviceType, getApiCallHostPath, getZinoDomainAppData } from "./utils/apiCallFunction";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import WelcomePage from "./pages/welcome/WelcomePage";
import LoginPage from "./pages/login/LoginPage";
import ForgetPassword from "./pages/login/ForgetPassword";
import ResetPassword from "./pages/login/ResetPassword";
import OTPScreen from "./pages/login/OTPScreen";
import Page404 from "./pages/other/Page404";
import AppList from "./pages/AppList";
import NoAccessPage from "./pages/NoAccessPage";
import { regenerateToken, setIsMobileUserAgent, updateUserDataFunction } from "./redux/userview/userviewaction";
import { useDispatch, useSelector } from "react-redux";
import LogoutPage from "./pages/login/LogoutPage";
import PublicScreens from "./pages/publicScreens/PublicScreens";
import useTokenRefresher from "./Hook/useTokenRefresher";
import { APP_ENVIRONMENT } from "./Constants/Constants";
import SAML from "./pages/login/ExternalService/SAML";
import { SAML_TOKEN_PAGE } from "./Routes/Routes";
import "./Hook/useInstallPrompt"; // Activate module-level beforeinstallprompt listener early

function App() {
  const { user, authenticate } = useSelector((state) => state.userviewstore);
  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const loadDomain = async () => {
    try {
      setIsLoading(true);
      let resData = {
        favicon: "",
        title: "Zino",
        app_logo: ""
      };
      if (getApiCallHostPath()) {
        const encodedDomain = btoa(window.location.host);
        let { data } = await axios.get(`${getApiCallHostPath()}/api/v1/web-tab-title/${encodedDomain}`);
        resData = data?.data || {
          favicon: "",
          title: "Zino",
          app_logo: ""
        };
      }
      if (!resData.title) {
        resData.title = "Zino";
      }
      setStorageItem("Zino_domain_app_data", JSON.stringify(resData));
    } catch (error) {
      setStorageItem(
        "Zino_domain_app_data",
        JSON.stringify({
          favicon: "",
          title: "Zino",
          app_logo: ""
        })
      );
    } finally {
      let fetchedData = getZinoDomainAppData();
      //new title
      document.title = fetchedData.title;
      //new favicon
      const newFavicon = document.createElement("link");
      newFavicon.rel = "icon";
      if (fetchedData.favicon) {
        newFavicon.href = `${getApiCallHostPath()}/api/v1/public/${fetchedData.favicon}`;
      } else {
        newFavicon.href = `/zino-logo.png`;
      }
      const oldFavicon = document.querySelector('link[rel="icon"]');
      if (oldFavicon) {
        oldFavicon.remove();
      }
      document.head.appendChild(newFavicon);

      //new apple logo
      const newAppleIcon = document.createElement("link");
      newAppleIcon.rel = "apple-touch-icon";
      if (fetchedData.app_logo) {
        newAppleIcon.href = `${getApiCallHostPath()}/api/v1/public/${fetchedData.app_logo}`;
      } else {
        newAppleIcon.href = `/zino-logo.png`;
      }
      const oldAppleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (oldAppleIcon) {
        oldAppleIcon.remove();
      }
      document.head.appendChild(newAppleIcon);

      //new manifest — created here so the browser only sees the final manifest after API data is ready
      const oldManifest = document.getElementById("new_manifest");
      if (oldManifest) {
        oldManifest.remove();
      }
      const manifestLink = document.createElement("link");
      manifestLink.rel = "manifest";
      manifestLink.id = "new_manifest";
      if (fetchedData.app_logo) {
        const manifest = {
          short_name: fetchedData.title,
          name: fetchedData.title,
          icons: [
            {
              src: `${getApiCallHostPath()}/api/v1/public/${fetchedData.app_logo}`,
              sizes: "192x192 512x512",
              type: "image/png"
            }
          ],
          start_url: window.location.origin,
          display: "standalone",
          theme_color: "#000000",
          background_color: "#ffffff"
        };
        manifestLink.href = `data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(manifest))}`;
      } else {
        manifestLink.href = "/manifest.json";
      }
      document.head.appendChild(manifestLink);
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    return () => {
      const manifestLink = document.getElementById("new_manifest");
      if (manifestLink) {
        document.head.removeChild(manifestLink);
      }
    };
  }, []);

  useLayoutEffect(() => {
    loadDomain();
    let environmentValue = APP_ENVIRONMENT || "local";

    if (environmentValue === "local") {
      let Zino_Is_Mobile_User_Agent = getStorageItem("Zino_Is_Mobile_User_Agent");

      if (Zino_Is_Mobile_User_Agent) {
        dispatch(setIsMobileUserAgent(Zino_Is_Mobile_User_Agent === "true"));
      } else {
        dispatch(setIsMobileUserAgent(detectDeviceType() === "Mobile"));
      }
    } else {
      dispatch(setIsMobileUserAgent(detectDeviceType() === "Mobile"));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (["/resetPassword", "/logout", "/public/applist", SAML_TOKEN_PAGE].includes(location.pathname)) {
      setIsLoading(false);
    } else if (!authenticate && !user) {
      if (!authenticate) dispatch(updateUserDataFunction());
    } else {
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticate, user]);

  useTokenRefresher(user?.refresh_time, user?.exp_time, () => {
    dispatch(regenerateToken());
    setIsLoading(false);
  });

  if (isLoading) {
    return <ProcessBarSection />;
  } else {
    return (
      <>
        <Routes>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/logout" element={<LogoutPage />} />
          <Route path="/forgotPassword" element={<ForgetPassword />} />
          <Route path="/resetPassword" element={<ResetPassword />} />
          <Route path="/otp" element={<OTPScreen />} />
          <Route path="/page404" element={<Page404 />} />
          <Route path="/applist" element={<AppList />} />
          <Route path="/public/applist" element={<PublicScreens />} />
          <Route path="/noaccess" element={<NoAccessPage />} />
          <Route path={SAML_TOKEN_PAGE} element={<SAML />} />
          <Route path="/*" element={<Navigate to="/login" replace={true} />} />
        </Routes>
      </>
    );
  }
}

export default App;
